import { type JSX, useEffect, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import type { KiVorschlag } from '../api/ki';
import {
  STATUSNAME,
  VORGANGSSTATUS,
  antworten,
  antwortvorlage,
  bausteinAusfuellen,
  bausteinauswahl,
  kiEntscheiden,
  kiEntwerfen,
  kiPostfachstand,
  zuordnung as ladeZuordnung,
  type Antwortdatei,
  type Antwortvorlage,
  type Bearbeitungsansicht,
  type Bezugszeile,
  type KiAntwortentwurf,
  type KiVerfuegbarkeit,
  type Textbaustein,
  type Vorgangsstatus,
  type Vorgangszeile,
} from '../api/support';
import { Kikennzeichnung } from '../bericht/KiBericht';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { ANHANG_GRENZE, adressen, alsBase64, fange, groesse, offenePlatzhalter } from './bearbeitungshilfe';
import { KiQuellen, type Quellenwege } from './KiBereich';

/**
 * DIE ANTWORT AUS DEM VORGANG (SUP-19, SUP-20, SUP-22; Auftrag CRM-178).
 *
 * An, Kopie und Betreff kommen aus der Vorlage des Servers; der Text wird
 * geschrieben oder aus einem Textbaustein eingesetzt. Das Zitat enthält nur
 * den Kundenverkehr — nie eine Notiz (SUP-21), das stellt der Server sicher.
 * Gesendet wird erst nach der Rückfrage; die Antwort geht in den Postausgang,
 * der Versand läuft im Hintergrund.
 *
 * SUP-22: Der Entwurf trägt den Stand der Ansicht, auf der er entstand. Hat
 * inzwischen jemand anderes geantwortet, lehnt der Server ab (409) — die
 * Meldung steht über dem Entwurf, der Verlauf wird neu geladen und der
 * Entwurf bleibt, wie er ist.
 *
 * KI-ENTWURF (SUP-29 bis SUP-33, SUP-40; CRM-179): Der Entwurf kommt ins
 * Textfeld und ist frei bearbeitbar; die Quellen stehen darunter. Sätze mit
 * Geldwirkung sind markiert und verlangen vor dem Senden ein eigenes Häkchen
 * (SUP-33). Mit dem Senden gilt der Entwurf als übernommen — unverändert oder
 * geändert, und wie stark, hält der Server intern fest (SUP-40); beim Kunden
 * steht davon nichts.
 */

type Hochgeladen = Antwortdatei & { readonly groesse: number };

/** Der Status nach dem Senden; „neu" ergibt nach einer Antwort keinen Sinn. */
const FOLGESTATUS = VORGANGSSTATUS.filter((s) => s !== 'neu');

export function Antwort({
  vorgang: v,
  ansicht,
  beiGesendet,
  beiKonflikt,
  beiSchliessen,
  quellenwege = {},
}: {
  readonly vorgang: Vorgangszeile;
  readonly ansicht: Bearbeitungsansicht;
  /** KI-03: wohin die Quellen eines KI-Entwurfs führen. */
  readonly quellenwege?: Quellenwege | undefined;
  /** Lädt Vorgang und Bearbeitung neu und schließt den Entwurf. */
  readonly beiGesendet: () => Promise<void>;
  /** Lädt neu und gibt den neuen Stand zurück — der Entwurf bleibt offen. */
  readonly beiKonflikt: () => Promise<string | null>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const kennung = useId();
  const [vorlage, setzeVorlage] = useState<Antwortvorlage | null>(null);
  const [an, setzeAn] = useState('');
  const [kopie, setzeKopie] = useState('');
  const [betreff, setzeBetreff] = useState('');
  const [text, setzeText] = useState('');
  const [zitat, setzeZitat] = useState(true);
  const [anhaenge, setzeAnhaenge] = useState<ReadonlySet<string>>(new Set());
  const [dateien, setzeDateien] = useState<readonly Hochgeladen[]>([]);
  // SUP-19: nach der Antwort wartet der Vorgang in der Regel auf den Kunden.
  const [status, setzeStatus] = useState<Vorgangsstatus>('wartet_auf_kunde');
  const [bausteine, setzeBausteine] = useState<readonly Textbaustein[]>([]);
  const [baustein, setzeBaustein] = useState('');
  // SUP-22: der Stand, auf dem der Entwurf entstand.
  const [stand, setzeStand] = useState(ansicht.stand);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [konflikt, setzeKonflikt] = useState<ApiFehler | null>(null);
  const [frage, setzeFrage] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [kiStand, setzeKiStand] = useState<KiVerfuegbarkeit | null>(null);
  const [ki, setzeKi] = useState<KiVorschlag<KiAntwortentwurf> | null>(null);
  const [kiBezuege, setzeKiBezuege] = useState<readonly Bezugszeile[]>([]);
  const [geldBestaetigt, setzeGeldBestaetigt] = useState(false);
  const [geldPflicht, setzeGeldPflicht] = useState(false);

  useEffect(() => {
    let gilt = true;
    // KI-07: Was die KI hier kann; scheitert die Abfrage, bleibt die Antwort ohne KI schreibbar.
    kiPostfachstand(v.id).then(
      (s) => {
        if (!gilt) return;
        setzeKiStand(s.antwort);
        if (s.offenerEntwurf !== null) {
          const offen = s.offenerEntwurf;
          setzeKi(offen);
          setzeText((alt) => (alt.trim() === '' ? offen.entwurf.text : alt));
          setzeGeldPflicht(s.offenerEntwurf.entwurf.geldzusagen.length > 0);
        }
        // KI-03: Buchungen öffnen sich über ihre Nummer aus den Bezügen.
        if (s.antwort.verfuegbar || s.offenerEntwurf !== null) {
          ladeZuordnung(v.id).then(
            (z) => {
              if (gilt) setzeKiBezuege(z.bezuege);
            },
            () => undefined,
          );
        }
      },
      () => {
        if (gilt) setzeKiStand(null);
      },
    );
    return () => {
      gilt = false;
    };
  }, [v.id]);

  useEffect(() => {
    let gilt = true;
    antwortvorlage(v.id).then(
      (vl) => {
        if (!gilt) return;
        setzeVorlage(vl);
        setzeAn(vl.an.join(', '));
        setzeKopie(vl.kopie.join(', '));
        setzeBetreff(vl.betreff);
      },
      (f: unknown) => {
        if (gilt) fange(setzeFehler, f);
      },
    );
    // SUP-20: nur aktive Bausteine des Postfachs, passend zur Kategorie, wenn eine gesetzt ist.
    bausteinauswahl(v.postfach, ansicht.kategorie).then(
      (b) => {
        if (gilt) setzeBausteine(b);
      },
      () => {
        // Ohne Bausteine bleibt die Antwort schreibbar; die Auswahl fehlt dann.
        if (gilt) setzeBausteine([]);
      },
    );
    return () => {
      gilt = false;
    };
    // Die Vorlage gilt für den geöffneten Entwurf; eine neue Kategorie lädt die Bausteine nicht neu.
  }, [v.id, v.postfach]);

  const offen = offenePlatzhalter(`${betreff}\n${text}`);
  const ablegbar = (vorlage?.anhaenge ?? []).filter((a) => a.zustand === 'abgelegt');
  const summe =
    ablegbar.filter((a) => anhaenge.has(a.id)).reduce((s, a) => s + a.groesse, 0)
    + dateien.reduce((s, d) => s + d.groesse, 0);
  const zuGross = summe > ANHANG_GRENZE;
  const empfaenger = adressen(an);

  const sperre = !ansicht.versandbereit
    ? 'Das Postfach kann gerade nicht senden: Es ist nicht aktiv, oder die Zugangsdaten zum Senden fehlen. Die Administration richtet es ein; bis dahin lässt sich die Antwort schreiben, aber nicht senden.'
    : empfaenger.length === 0
      ? 'Ohne Empfänger lässt sich nicht senden.'
      : text.trim() === ''
        ? 'Der Text ist leer.'
        : zuGross
          ? 'Die Anhänge sind zusammen größer als 3 MB.'
          : null;

  const einsetzen = async (): Promise<void> => {
    if (baustein === '') return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const b = await bausteinAusfuellen(v.id, baustein);
      setzeText((alt) => (alt.trim() === '' ? b.text : `${alt.trimEnd()}\n\n${b.text}`));
      if (b.betreff !== null && betreff.trim() === '') setzeBetreff(b.betreff);
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const kiEinsetzen = (e: KiVorschlag<KiAntwortentwurf>): void => {
    setzeKi(e);
    setzeGeldBestaetigt(false);
    setzeGeldPflicht(e.entwurf.geldzusagen.length > 0);
    setzeText((alt) => (alt.trim() === '' ? e.entwurf.text : `${alt.trimEnd()}\n\n${e.entwurf.text}`));
  };

  const kiErzeugen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      // Ein noch offener eigener Entwurf wird ersetzt — er gilt als verworfen (KI-08).
      if (ki !== null) await kiEntscheiden(ki.id, 'verwerfen');
      const neu = await kiEntwerfen(v.id);
      if (ki !== null && text.trim() === ki.entwurf.text.trim()) setzeText('');
      kiEinsetzen(neu);
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const kiVerwerfen = async (): Promise<void> => {
    if (ki === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await kiEntscheiden(ki.id, 'verwerfen'); // KI-04, KI-08
      if (text.trim() === ki.entwurf.text.trim()) setzeText('');
      setzeKi(null);
      setzeGeldPflicht(false);
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const dateienHinzu = async (liste: FileList | null): Promise<void> => {
    if (liste === null) return;
    const neu: Hochgeladen[] = [];
    for (const datei of Array.from(liste)) {
      neu.push({
        dateiname: datei.name,
        inhaltstyp: datei.type === '' ? 'application/octet-stream' : datei.type,
        inhaltBase64: await alsBase64(datei),
        groesse: datei.size,
      });
    }
    setzeDateien((alt) => [...alt, ...neu]);
  };

  const senden = async (): Promise<void> => {
    try {
      await antworten(v.id, {
        an: empfaenger,
        kopie: adressen(kopie),
        betreff: betreff.trim(),
        text,
        zitat,
        anhaenge: ablegbar.filter((a) => anhaenge.has(a.id)).map((a) => a.id),
        dateien: dateien.map(({ dateiname, inhaltstyp, inhaltBase64 }) => ({ dateiname, inhaltstyp, inhaltBase64 })),
        stand,
        status,
        ...(ki === null ? {} : { ki: { vorschlag: ki.id, geldzusageBestaetigt: geldBestaetigt } }),
      });
    } catch (f) {
      // SUP-33: Im geänderten Text steht etwas mit Geldwirkung — erst bestätigen.
      if (f instanceof ApiFehler && f.kennung === 'ki_geldzusage_unbestaetigt') {
        setzeFrage(false);
        setzeGeldPflicht(true);
        setzeFehler(f);
        return;
      }
      // SUP-22: Jemand anderes hat inzwischen geantwortet. Entwurf behalten, Verlauf neu laden.
      if (f instanceof ApiFehler && f.status === 409) {
        setzeFrage(false);
        setzeKonflikt(f);
        const neu = await beiKonflikt();
        if (neu !== null) setzeStand(neu);
        return;
      }
      throw f;
    }
    setzeFrage(false);
    await beiGesendet();
  };

  return (
    <section className="vorgang__antwort" aria-label="Antwort">
      <h3>Antwort</h3>
      {konflikt !== null && (
        <>
          <Meldung fehler={konflikt} />
          <p className="warnung" role="status">
            Der Vorgang ist inzwischen beantwortet oder geändert worden. Der Verlauf ist neu geladen, der Entwurf
            steht unverändert hier — erst lesen, dann erneut senden.
          </p>
        </>
      )}
      {fehler !== null && <Meldung fehler={fehler} />}

      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${kennung}-an`}>
          An <span className="feld__pflicht">Pflicht</span>
        </label>
        <input id={`${kennung}-an`} className="feld__eingabe" value={an} onChange={(e) => setzeAn(e.target.value)} />
        {vorlage !== null && <p className="feld__hilfe">Gesendet wird von {vorlage.absender}.</p>}
      </div>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${kennung}-kopie`}>Kopie</label>
        <input id={`${kennung}-kopie`} className="feld__eingabe" value={kopie} onChange={(e) => setzeKopie(e.target.value)} />
        <p className="feld__hilfe">Mehrere Adressen mit Komma trennen.</p>
      </div>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${kennung}-betreff`}>Betreff</label>
        <input id={`${kennung}-betreff`} className="feld__eingabe" value={betreff} onChange={(e) => setzeBetreff(e.target.value)} />
      </div>

      {/* SUP-20: Textbaustein wählen und einsetzen. */}
      {bausteine.length > 0 && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-baustein`}>Textbaustein</label>
          <select
            id={`${kennung}-baustein`}
            className="feld__eingabe"
            value={baustein}
            onChange={(e) => setzeBaustein(e.target.value)}
          >
            <option value="">— wählen —</option>
            {bausteine.map((b) => (
              <option key={b.id} value={b.id}>
                {b.titel}
              </option>
            ))}
          </select>{' '}
          <button
            type="button"
            className="knopf knopf--klein"
            disabled={laeuft || baustein === ''}
            onClick={() => void einsetzen()}
          >
            Einsetzen
          </button>
        </div>
      )}

      {/* SUP-29: der KI-Entwurf, wenn Postfach, Rolle und Schalter ihn erlauben (KI-06, KI-07). */}
      {kiStand !== null && (
        <div className="feld">
          {kiStand.verfuegbar ? (
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void kiErzeugen()}>
              {ki === null ? 'KI-Entwurf erstellen' : 'Neuen KI-Entwurf erstellen'}
            </button>
          ) : (
            <p className="hilfe">KI-Entwurf nicht verfügbar: {kiStand.hinweis}</p>
          )}
        </div>
      )}
      {ki !== null && (
        <div className="feld vorgang__kientwurf">
          <Kikennzeichnung kennzeichnung={ki.kennzeichnung} />
          {/* SUP-33: Geldzusagen markiert. */}
          {ki.entwurf.geldzusagen.length > 0 && (
            <div className="warnung" role="status">
              <p>Der Entwurf enthält Aussagen mit Geldwirkung — vor dem Senden prüfen:</p>
              <ul>
                {ki.entwurf.geldzusagen.map((g) => (
                  <li key={g}>
                    <mark>{g}</mark>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <details>
            <summary>Quellen des Entwurfs</summary>
            <KiQuellen quellen={ki.quellen} bezuege={kiBezuege} wege={quellenwege} />
          </details>
          <div className="knopfreihe">
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void kiVerwerfen()}>
              KI-Entwurf verwerfen
            </button>
          </div>
        </div>
      )}

      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${kennung}-text`}>
          Text <span className="feld__pflicht">Pflicht</span>
        </label>
        <textarea
          id={`${kennung}-text`}
          className="feld__eingabe"
          rows={10}
          value={text}
          onChange={(e) => setzeText(e.target.value)}
        />
      </div>
      {offen.length > 0 && (
        <p className="warnung" role="status">
          Noch offene Platzhalter: {offen.map((p) => `[[${p}]]`).join(', ')}. Vor dem Senden durch den richtigen Wert
          ersetzen — sonst stehen sie so beim Kunden.
        </p>
      )}

      <div className="feld">
        <label className="liste__schalter">
          <input type="checkbox" checked={zitat} onChange={(e) => setzeZitat(e.target.checked)} />
          <span>Verlauf zitieren</span>
        </label>
        {zitat && vorlage !== null && vorlage.zitat !== '' && (
          <pre className="vorgang__zitat" aria-label="Vorschau des Zitats">
            {vorlage.zitat}
          </pre>
        )}
      </div>

      <fieldset className="feld">
        <legend className="feld__beschriftung">Anhänge</legend>
        {ablegbar.map((a) => (
          <label key={a.id} className="liste__schalter">
            <input
              type="checkbox"
              checked={anhaenge.has(a.id)}
              onChange={(e) => {
                const neu = new Set(anhaenge);
                if (e.target.checked) neu.add(a.id);
                else neu.delete(a.id);
                setzeAnhaenge(neu);
              }}
            />
            <span>
              {a.dateiname} <span className="hilfe">{groesse(a.groesse)}</span>
            </span>
          </label>
        ))}
        {dateien.length > 0 && (
          <ul className="kanalliste" aria-label="Hochgeladene Dateien">
            {dateien.map((d, i) => (
              <li key={`${d.dateiname}-${String(i)}`}>
                {d.dateiname} <span className="hilfe">{groesse(d.groesse)}</span>{' '}
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => setzeDateien((alt) => alt.filter((_, j) => j !== i))}
                >
                  Weglassen
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="feld__beschriftung" htmlFor={`${kennung}-dateien`}>Dateien hinzufügen</label>
        <input
          id={`${kennung}-dateien`}
          type="file"
          multiple
          onChange={(e) => {
            const liste = e.target.files;
            void dateienHinzu(liste).finally(() => {
              e.target.value = '';
            });
          }}
        />
        {/* SUP-19: die Grenze gilt für alle Anhänge zusammen. */}
        <p className={zuGross ? 'feld__fehler' : 'feld__hilfe'}>
          Zusammen {groesse(summe)} von höchstens 3 MB.
        </p>
      </fieldset>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${kennung}-status`}>Status danach</label>
        <select
          id={`${kennung}-status`}
          className="feld__eingabe"
          value={status}
          onChange={(e) => setzeStatus(e.target.value as Vorgangsstatus)}
        >
          {FOLGESTATUS.map((s) => (
            <option key={s} value={s}>
              {STATUSNAME[s]}
            </option>
          ))}
        </select>
      </div>

      {ki !== null && geldPflicht && (
        <div className="feld">
          <label className="liste__schalter">
            <input type="checkbox" checked={geldBestaetigt} onChange={(e) => setzeGeldBestaetigt(e.target.checked)} />
            <span>Ich habe die Aussagen mit Geldwirkung geprüft und sage sie so zu.</span>
          </label>
        </div>
      )}
      {sperre !== null && (
        <p className={ansicht.versandbereit ? 'feld__hilfe' : 'warnung'} role="status">
          {sperre}
        </p>
      )}
      <Abschlussreihe
        abbruch={
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Abbrechen
          </button>
        }
      >
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={sperre !== null || laeuft || (ki !== null && geldPflicht && !geldBestaetigt)}
          onClick={() => setzeFrage(true)}
        >
          Senden …
        </button>
      </Abschlussreihe>

      {frage && (
        <Rueckfrage
          frage="Antwort senden?"
          unterzeile={`An ${empfaenger.join(', ')}`}
          tat="Senden"
          beiBestaetigen={senden}
          beiSchliessen={() => setzeFrage(false)}
        >
          <p>
            Die Antwort geht an den Kunden und lässt sich danach nicht zurückholen. Der Vorgang steht dann auf
            „{STATUSNAME[status]}".
          </p>
          {offen.length > 0 && (
            <p className="warnung">Im Text stehen noch offene Platzhalter: {offen.map((p) => `[[${p}]]`).join(', ')}.</p>
          )}
        </Rueckfrage>
      )}
    </section>
  );
}
